import ResetPasswordImg from "@/assets/img/loginRegister.png";
import LogoLight from "@/assets/img/logo-light.png";
import Spinner from "@/components/loader/Spinner";
import WarningBanner from "@/components/banners/WarningBanner";
import axiosInstanceNoAuth from "@/utils/axiosConfigNoAuth";
import axios, { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [showBanner, setShowBanner] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    // Initialize any needed services or listeners here
  }, []);

  const handleResetPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);

    if (!navigator.onLine) {
      setShowBanner(true);
      setAnimateOut(false);
      setTimeout(() => setAnimateOut(true), 3000);
      setIsLoading(false);
      return;
    }

    try {
      const apiUrl = import.meta.env.VITE_APP_REDBIRDPOSBE_RESET_PASSWORD ?? "";
      const response = await axiosInstanceNoAuth.post(apiUrl, { email });

      if (response.data) {
        setIsSuccess(true);
      }
      setIsLoading(false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response && axiosError.response.status === 500) {
          setShowBanner(true);
        } else {
          console.error("Reset password error", axiosError);
        }
      } else {
        console.error("Reset password error", error);
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (showBanner) {
      setAnimateOut(false);
      const timerId = setTimeout(() => {
        setAnimateOut(true);
        setTimeout(() => setShowBanner(false), 500);
      }, 3000);

      return () => clearTimeout(timerId);
    }
  }, [showBanner]);

  return (
    <div className="flex min-h-screen overflow-hidden">
      <Link to="/" className="absolute left-0 top-0 p-4">
        <img src={LogoLight} alt="Home" className="w-30 h-12" />
      </Link>
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <Spinner />
        </div>
      )}
      <div className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-20">
          <div>
            {showBanner && (
              <WarningBanner
                title={t("account.reset.invalidEmail")}
                text={t("account.reset.invalidEmailMessage")}
                className={`${
                  animateOut ? "animate-slideOutRight" : "animate-slideDown"
                }`}
              />
            )}
            {isSuccess && (
              <div
                className="relative rounded border border-green-400 bg-green-100 px-4 py-3 text-green-700"
                role="alert"
              >
                <strong className="font-bold">
                  {t("account.reset.success")}
                </strong>
                <span className="block sm:inline">
                  {t("account.reset.successMessage")}
                </span>
              </div>
            )}
            <h2 className="mb-8 mt-6 text-center text-3xl font-bold text-gray-900 md:text-5xl">
              {t("account.reset.welcome")}
            </h2>
            <div className="text-center text-2xl font-extrabold md:text-3xl">
              <span className="text-blue-500">
                {t("account.reset.headline")}&nbsp;
              </span>
            </div>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleResetPassword}>
            <div>
              <label
                htmlFor="email"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                {t("account.reset.email_label")}{" "}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="relative block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
                placeholder={t("account.reset.email_placeholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <button
                type="submit"
                className="group relative flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                {t("account.reset.reset_button")}
              </button>
            </div>
          </form>
          <div className="text-center text-sm font-medium">
            {t("account.reset.remembered")}{" "}
            <Link to="/" className="ml-2 font-medium text-red-600">
              {t("account.reset.login")}
            </Link>
          </div>
        </div>
      </div>

      <div className="relative hidden w-1/2 lg:block">
        <div className="absolute bottom-0 right-0 top-0 w-3/5 bg-red-600"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src={ResetPasswordImg}
            className="object-contain"
            style={{ maxHeight: "80%" }}
            alt="Reset Password Visual"
          />
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
